import React, { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { getServer, getUser } from '../actions/auth';
import { routes } from '#app/constants/routes';
import { Tracker } from 'finhelper';
import { NotifyContainer } from 'notify-component';
import ErrorBoundary from '../interface/error/ErrorBoundary';
import { TooltipProvider } from '#app/providers/TooltipProvider';
import CallbackNotification from '#app/interface/notification/CallbackNotification';
import { useAppDispatch } from '#app/hooks/useAppStore';
import { usePagesRouterState } from './hooks/usePagesRouterState';
import { maintenanceDetected } from '#app/actions/maintenance';
import { MAINTENANCE_DETECTED_EVENT, MAINTENANCE_PENDING_FLAG } from '#app/helpers/maintenanceMode';

// 🔹 Ленивая загрузка тяжёлых страниц
const MainPage = React.lazy(() => import('#app/pages/MainPage/MainPage'));
const QuizPage = React.lazy(() => import('#app/pages/MainPage/QuizPage'));
const Robo = React.lazy(() => import('#app/pages/Robo/Robo'));
const Forbidden = React.lazy(() => import('../interface/error/Forbidden'));
const NotFound = React.lazy(() => import('../interface/error/NotFound'));
const Maintenance = React.lazy(() => import('../interface/error/Maintenance'));
const CabinetRoutesWrapper = React.lazy(() => import('./CabinetRoutesWrapper'));
const LandingLayout = React.lazy(() => import('./LandingLayout'));

// 🔹 Универсальная заглушка для загрузки чанков
const Fallback = () => <div></div>;

const PagesRouter = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [isActive, setActive] = useState(undefined);
  const [redirected, setRedirected] = useState(false);

  const { auth, authData, client, demo, isLoaded, maintenanceMode } = usePagesRouterState();

  useLayoutEffect(() => {
    const onMaintenanceDetected = () => {
      // Сбрасываем pending-флаг: событие уже обработано и больше не должно дублироваться.
      window[MAINTENANCE_PENDING_FLAG] = false;
      dispatch(maintenanceDetected());
    };

    window.addEventListener(MAINTENANCE_DETECTED_EVENT, onMaintenanceDetected);

    // Если событие пришло до подписки, забираем его из pending-флага.
    if (window[MAINTENANCE_PENDING_FLAG]) {
      window[MAINTENANCE_PENDING_FLAG] = false;
      dispatch(maintenanceDetected());
    }

    return () => {
      window.removeEventListener(MAINTENANCE_DETECTED_EVENT, onMaintenanceDetected);
    };
  }, [dispatch]);

  useLayoutEffect(() => {
    Tracker.init();
    dispatch(getServer());
    dispatch(
      getUser({
        callback: () => setActive(true),
        fallback: () => setActive(true),
      })
    );
  }, [dispatch]);

  useEffect(() => {
    Tracker.hit(location.pathname);
  }, [location]);

  useEffect(() => {
    if (auth && !redirected) {
      if (maintenanceMode) {
        return;
      }

      setRedirected(true);
      if (!routes.isCabinetPath(location.pathname)) {
        navigate(routes.accounting.dashboard, { replace: true });
      }
    }
  }, [auth, navigate, redirected, location.pathname, maintenanceMode]);

  useEffect(() => {
    if (maintenanceMode) {
      return;
    }

    if (auth && location.pathname === routes.CABINET) {
      navigate(routes.accounting.dashboard, { replace: true });
    }
  }, [auth, navigate, location.pathname, maintenanceMode]);

  if (typeof isActive === 'undefined') {
    return null;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<Fallback />}>
        <Routes>
          {maintenanceMode ? (
            <Route path="*" element={<Maintenance />} />
          ) : (
            <>
              {auth ? (
                <Route
                  path="/cabinet/*"
                  element={
                    <Suspense fallback={<Fallback />}>
                      <CabinetRoutesWrapper
                        authData={authData}
                        isLoaded={isLoaded}
                        client={client}
                        demo={demo}
                        scenarioName={client?.scenario?.name}
                      />
                    </Suspense>
                  }
                />
              ) : (
                <Route path="/cabinet" element={<Navigate to={routes.LOGIN} />} />
              )}

              <Route path="/" element={<LandingLayout />}>
                <Route index element={<MainPage />} />
                <Route path="roboadvisor" element={<MainPage />} />
                <Route path="robo" element={<Robo />} />
                <Route path="agreement" element={<MainPage />} />
                <Route path="policy" element={<MainPage />} />
                <Route path="login" element={<Forbidden />} />
                <Route path="quiz" element={<QuizPage />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </>
          )}
        </Routes>
        <CallbackNotification />
        <NotifyContainer />
        <TooltipProvider />
      </Suspense>
    </ErrorBoundary>
  );
};

export default PagesRouter;
